import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { trackPurchase } from "../lib/metaPixel";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const affirmOrder = params.get("affirm_order");
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const [info, setInfo] = useState(null);
  const { clear } = useCart();
  const purchaseTrackedRef = useRef(false);

  useEffect(() => {
    if (!sessionId && !affirmOrder) { setState("error"); return; }
    let attempts = 0;
    let timer;
    const statusUrl = affirmOrder
      ? `/affirm/status/${affirmOrder}`
      : `/payments/status/${sessionId}`;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(statusUrl);
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("paid");
          if (!purchaseTrackedRef.current) {
            purchaseTrackedRef.current = true;
            trackPurchase({ value: data.amount });
          }
          clear();
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch {
        setState("error"); return;
      }
      if (attempts >= 10) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, affirmOrder]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-alt px-6" data-testid="payment-success-page">
      <div className="w-full max-w-md rounded-3xl border border-line bg-white p-10 text-center">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-crimson" />
            <h1 className="mt-6 font-display text-2xl font-bold text-ink">Confirming your payment…</h1>
            <p className="mt-2 text-muted-foreground">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-600" />
            <h1 className="mt-6 font-display text-3xl font-black tracking-tighter text-ink">Payment successful</h1>
            <p className="mt-3 text-muted-foreground">
              Thank you for your purchase{info?.product_name ? ` of the ${info.product_name}` : ""}. We'll ship your PhoneCheck-certified device free via UPS.
            </p>
            {info?.amount != null && <div className="mt-4 font-display text-xl font-bold text-ink">${info.amount}</div>}
            <Link to="/products" data-testid="success-continue" className="mt-8 inline-block rounded-full bg-crimson px-8 py-3 font-bold text-white hover:bg-crimson-hover">
              Continue shopping
            </Link>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <XCircle className="mx-auto h-14 w-14 text-crimson" />
            <h1 className="mt-6 font-display text-2xl font-bold text-ink">
              {state === "timeout" ? "Still processing" : "Something went wrong"}
            </h1>
            <p className="mt-2 text-muted-foreground">
              {state === "timeout"
                ? "Your payment is taking longer than expected. If you were charged, you'll receive a confirmation shortly."
                : "We couldn't confirm this payment. If you were charged, please contact us."}
            </p>
            <Link to="/contact" data-testid="success-contact" className="mt-8 inline-block rounded-full border border-ink px-8 py-3 font-bold text-ink hover:bg-ink hover:text-white">
              Contact support
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
